import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DiscordChannelPicker } from '@/components/discord/DiscordPickers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api';
import { useGiveaways, useCreateGiveaway, useDeleteGiveaway } from '@/lib/queries';
import { useCan } from '@/lib/use-can';
import { formatDate } from '@/lib/utils';

function defaultEndsAtLocal(): string {
  // 24h from now, formatted for <input type="datetime-local">.
  const d = new Date(Date.now() + 24 * 3600_000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function GiveawaysListPage() {
  const can = useCan();
  const list = useGiveaways();
  const create = useCreateGiveaway();
  const remove = useDeleteGiveaway();

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [prize, setPrize] = useState('');
  const [channelId, setChannelId] = useState('');
  const [endsAt, setEndsAt] = useState(defaultEndsAtLocal());

  if (!can('giveaways.manage')) {
    return <p className="text-muted-foreground">You don't have access to this page.</p>;
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!channelId) {
      toast.error('Pick a channel');
      return;
    }
    const when = new Date(endsAt);
    if (!Number.isFinite(when.getTime()) || when.getTime() <= Date.now()) {
      toast.error('End time must be in the future');
      return;
    }
    try {
      await create.mutateAsync({
        name,
        prize,
        embed: {
          title: '🎉 Giveaway: {prize}',
          description: 'Click the 🎉 button below to enter!',
          color: 0x5865f2,
        },
        channelId,
        winnerCount: 1,
        endsAt: when.toISOString(),
        requiredRoleId: null,
        mention: null,
      });
      toast.success('Draft created');
      setOpen(false);
      setName('');
      setPrize('');
      setChannelId('');
      setEndsAt(defaultEndsAtLocal());
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  async function handleDelete(id: string, label: string, published: boolean) {
    const msg = published
      ? `Delete giveaway "${label}" AND remove the posted message?`
      : `Delete giveaway "${label}"?`;
    if (!confirm(msg)) return;
    try {
      await remove.mutateAsync({ id, unpublish: published });
      toast.success('Deleted');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Error');
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Giveaways</h1>
          <p className="text-muted-foreground">Create, schedule, and draw giveaways</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New giveaway
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>New giveaway</DialogTitle>
                <DialogDescription>
                  Creates a draft. Tune the embed, winner count, and role gate on the detail page.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name (internal label)</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="prize">Prize</Label>
                  <Input
                    id="prize"
                    value={prize}
                    onChange={(e) => setPrize(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Channel</Label>
                  <DiscordChannelPicker value={channelId} onValueChange={setChannelId} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endsAt">Ends at (local time)</Label>
                  <Input
                    id="endsAt"
                    type="datetime-local"
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    required
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={create.isPending}>
                  Create
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{list.data?.length ?? 0} giveaway(s)</CardTitle>
          <CardDescription>Drafts, scheduled, active, and ended giveaways</CardDescription>
        </CardHeader>
        <CardContent>
          {list.data && list.data.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Prize</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Winners</TableHead>
                  <TableHead>Ends</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.data.map((g) => (
                  <TableRow key={g.id}>
                    <TableCell>
                      <Link to={`/giveaways/${g.id}`} className="font-medium hover:underline">
                        {g.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm">{g.prize}</TableCell>
                    <TableCell>
                      <Badge variant={g.status === 'active' ? 'default' : 'secondary'}>
                        {g.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{g.winnerCount}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {g.endsAt ? formatDate(g.endsAt) : '—'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(g.id, g.name, !!g.messageId)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {list.data?.length === 0 && (
            <p className="text-sm text-muted-foreground">No giveaways yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
